import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import TID from "@/lib/testIds";
import { useAuth } from "@/lib/auth";

const STEPS = [
  { key: "pan", label: "PAN", placeholder: "ABCPV1234D" },
  { key: "aadhaar", label: "Aadhaar", placeholder: "655675523712" },
  { key: "bank", label: "Bank account", placeholder: "account:026291800001191,ifsc:HDFC0001234" },
  { key: "upi", label: "UPI (optional)", placeholder: "success@upi" },
  { key: "selfie", label: "Self-video / selfie liveness", placeholder: "selfie-upload-token" },
];

export default function InfluencerKYS() {
  const { user } = useAuth();
  const [inf, setInf] = useState(null);
  const [values, setValues] = useState({});
  const load = useCallback(() =>
    api.get(`/influencers/${user.influencer_id}`).then((r) => { setInf(r.data); setValues({}); }),
  [user.influencer_id]);
  useEffect(() => { load(); }, [load]);

  if (!inf) return <div>Loading…</div>;

  const verify = async (key) => {
    const value = values[key] || inf.kys_checks?.[key]?.value || "";
    if (!value) return toast.error(`Enter your ${key.toUpperCase()}`);
    try {
      const { data } = await api.post("/kys/verify", { check_type: key, value });
      toast[data.ok ? "success" : "error"](data.reason);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    try { await api.post(`/kys/submit-review/${inf.id}`); toast.success("Submitted to ProPay Procurement Desk"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const allChecked = STEPS.every((s) => inf.kys_checks?.[s.key]?.ok);

  // Statuses from which the influencer is allowed to submit for compliance
  // review. Procurement-invited profiles start as "invited"; self-registered
  // profiles start as "pending"; and a rejected profile should be able to
  // resubmit after fixing the failing checks.
  const submitAllowedStatuses = ["pending", "invited", "rejected"];
  const canSubmit = allChecked && submitAllowedStatuses.includes(inf.compliance_status);

  return (
    <div className="space-y-6">
      <Section title="Know Your Supplier (KYS)" subtitle="Verify your identity and bank details via Cashfree Secure ID. Once verified, ProPay can issue your Purchase Orders."
        right={<StatusPill status={inf.compliance_status} />} />
      <div className="bg-white rounded-xl border p-6 space-y-4 max-w-3xl">
        {STEPS.map((s) => {
          const chk = inf.kys_checks?.[s.key];
          return (
            <div key={s.key} data-testid={TID.kysStep(s.key)} className="border-b pb-4 last:border-b-0">
              <div className="flex justify-between items-center mb-2">
                <div className="font-semibold text-slate-800">{s.label}</div>
                {chk && <span className={`text-xs font-bold ${chk.ok ? "text-emerald-600" : "text-rose-600"}`}>{chk.ok ? "VERIFIED" : "FAILED"}</span>}
              </div>
              <div className="flex gap-2">
                <Input data-testid={TID.kysInput(s.key)} placeholder={s.placeholder} value={values[s.key] ?? (chk?.value || "")} onChange={(e) => setValues({ ...values, [s.key]: e.target.value })} />
                <Button data-testid={TID.kysVerifyBtn(s.key)} onClick={() => verify(s.key)} variant="outline">Verify</Button>
              </div>
              {chk?.reason && <p className={`text-xs mt-1 ${chk.ok ? "text-emerald-700" : "text-rose-600"}`}>{chk.reason}</p>}
            </div>
          );
        })}
        <Button data-testid={TID.kysSubmitReview} disabled={!canSubmit} onClick={submit} className="w-full bg-[#0052FF] hover:bg-[#0043D9]">
          {inf.compliance_status === "in_review" ? "Submitted — awaiting compliance review" : inf.compliance_status === "verified" ? "You are verified" : allChecked ? "Submit for compliance review" : "Complete all checks first"}
        </Button>
      </div>
    </div>
  );
}
